import { BilletGroup } from "../models";
import { POSITION_CONSTANTS } from "../components/modals/edit-location-modal/constants/locationContentEdition";

/**
 * Calculates available positions for adding new billets to a location
 * @param currentGroups - Current billet groups in the location
 * @returns Array of all position numbers (including occupied ones, like old system)
 */
export const getAvailablePositions = (
  currentGroups: BilletGroup[],
  addFillingType?: string,
): number[] => {
  if (!currentGroups || currentGroups.length === 0) {
    // If no groups, position 0 is the first available position (matching old system)
    return [POSITION_CONSTANTS.FIRST_POSITION];
  }

  // OLD SYSTEM BEHAVIOR: Show ALL positions from 0 to groupsCount + 1
  // This matches the original BilletMoving.js logic:
  // for(var posIndex = 0; posIndex < WREF.LocContentEditionScreen.BL.Data.GroupsList.length + 1; posIndex++)
  // Users can select any position, including occupied ones (database handles insertion/shifting)

  const allPositions: number[] = [];

  // Add all positions from 0 to currentGroups.length (matching old system)
  for (
    let pos = POSITION_CONSTANTS.FIRST_POSITION;
    pos <= currentGroups.length;
    pos++
  ) {
    allPositions.push(pos);
  }

  // Sort order depends on AddFillingType to match operational expectations:
  // LIFO: New billets go to top of pile → show higher positions at top
  // FIFO: New billets go to bottom of pile → show lower positions at top
  if (addFillingType === POSITION_CONSTANTS.ADD_FILLING_TYPE_LIFO) {
    // LIFO: Higher positions at top (descending: 9,8,7,6,5,4,3,2,1,0)
    return allPositions.sort((a, b) => b - a);
  } else {
    // FIFO: Lower positions at top (ascending: 0,1,2,3,4,5,6,7,8,9)
    return allPositions.sort((a, b) => a - b);
  }
};

/**
 * Checks if a position is the next recommended position for new billets
 * @param position - Position to check
 * @param currentGroups - Current billet groups in the location
 * @param addFillingType - Location's AddFillingType (LIFO/FIFO)
 * @returns True if it's the next recommended position (for highlighting)
 */
export const isNextAvailablePosition = (
  position: number,
  currentGroups: BilletGroup[],
  addFillingType?: string,
): boolean => {
  if (!currentGroups || currentGroups.length === 0) {
    return position === POSITION_CONSTANTS.FIRST_POSITION;
  }

  // OLD SYSTEM BEHAVIOR: The "next" position is always the highest available position
  // which is currentGroups.length (equivalent to the last position + 1)
  // This matches the old system where positions 1,2,3,4,5 would show "6 (Next)"
  // Note: addFillingType parameter is kept for API compatibility but not used in old system logic
  const nextPosition = currentGroups.length;
  return position === nextPosition;
};
